import React, { useEffect, useState } from 'react';
import { CalButton } from './ui/CalButton';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageToggle } from './ui/LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';
import { navCopy } from '../data/translations/nav';
export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    language
  } = useLanguage();
  const copy = navCopy[language];
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Arriving from another page via "/#section": scroll there once the page has rendered
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const timer = window.setTimeout(() => scrollToSection(id), 150);
    return () => window.clearTimeout(timer);
  }, []);
  const handleNavClick = (event: React.MouseEvent<HTMLAnchorElement>, href: string, fromMobileMenu: boolean) => {
    const id = href.replace(/^\/?#/, '');
    // Section not on this page (e.g. on /start or /privacy): go to the homepage section
    if (!document.getElementById(id)) {
      event.preventDefault();
      window.location.href = `/#${id}`;
      return;
    }
    event.preventDefault();
    if (fromMobileMenu) {
      setMobileMenuOpen(false);
      // Wait for the menu to finish collapsing so the header height is final
      window.setTimeout(() => scrollToSection(id), 280);
    } else {
      scrollToSection(id);
    }
    window.history.replaceState(null, '', `#${id}`);
  };
  return <nav className={`fixed top-0 left-0 right-0 z-50 bg-paper transition-[padding] duration-300 ease-out ${isScrolled ? 'py-4' : 'py-6'}`} data-id="element-134" data-mp="1f5b6kfabw7bq">
      {/* Soft cream fade under the header so nothing below meets it in a hard line */}
      <div aria-hidden="true" className="header-fade absolute top-full left-0 right-0 h-12 pointer-events-none" data-id="element-135" data-mp="ugig896jaa3p"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-136" data-mp="145tlfkqauumb">
        <div className="flex justify-between items-center gap-4" data-id="element-137" data-mp="147wlyyp2w65a">
          <a href="/" className="flex flex-col items-start group flex-shrink-0" data-id="element-138" data-mp="ze231xharv79">
            <span className="font-serif text-2xl font-bold text-ink-900 tracking-wider group-hover:text-bronze-600 transition-colors duration-200" data-id="element-139" data-mp="1d5p3m9i9vpvu">
              MarkV Coaching
            </span>
            <span className="font-sans text-xs text-bronze-600 tracking-widest font-medium mt-0.5" data-id="element-140" data-mp="1n57hc87laffg">
              door Mark Vermeulen
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-5 lg:gap-7" data-id="element-141" data-mp="3ov99w921q78s">
            {copy.links.map(link => <a key={link.href} href={link.href} onClick={event => handleNavClick(event, link.href, false)} className="text-ink-900 hover:text-bronze-600 transition-colors text-sm uppercase tracking-widest font-medium whitespace-nowrap" data-id="element-142" data-mp="3jxzk2saqvtkf">
                {link.name}
              </a>)}

            <LanguageToggle data-id="element-143" data-mp="1unf6p7h3qhan" />

            <CalButton className="!px-5 !py-2 !text-sm whitespace-nowrap" data-id="element-144" data-mp="1umm6yqgk8s20">
              {copy.cta}
            </CalButton>
          </div>

          {/* Mobile Menu Button */}
          <button type="button" className="md:hidden p-2 -mr-2 text-ink-900 hover:text-bronze-600 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={copy.toggleMenu} aria-expanded={mobileMenuOpen} data-id="element-145" data-mp="1kr2dsbtds7gy">
            {mobileMenuOpen ? <X size={24} data-id="element-146" data-mp="314sk7p7z37tr" /> : <Menu size={24} data-id="element-147" data-mp="3152e3nw51z4q" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-148" data-mp="3619jnfkmh8m5">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="md:hidden bg-paper overflow-hidden" data-id="element-149" data-mp="161cp9yfz34d3">
            <div className="px-4 py-6 space-y-4 flex flex-col items-center" data-id="element-150" data-mp="s0a2fjh5lq14">
              <LanguageToggle className="!text-sm mb-2" data-id="element-151" data-mp="n2s22hh7cj4q" />

              {copy.links.map(link => <a key={link.href} href={link.href} className="text-ink-900 hover:text-bronze-600 transition-colors text-lg font-medium py-2" onClick={event => handleNavClick(event, link.href, true)} data-id="element-152" data-mp="ib9ydlnso4g2">
                  {link.name}
                </a>)}
              <CalButton className="w-full max-w-xs mt-4" onAfterClick={() => setMobileMenuOpen(false)} data-id="element-153" data-mp="4dldidtq8xb0">
                {copy.cta}
              </CalButton>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}

/** Smooth-scroll to a section, leaving room for the fixed header. */
function scrollToSection(id: string) {
  const target = document.getElementById(id);
  if (!target) return;
  const header = document.querySelector('nav');
  const offset = (header?.getBoundingClientRect().height ?? 80) + 8;
  const top = target.getBoundingClientRect().top + window.scrollY - offset;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top,
    behavior: reduceMotion ? 'auto' : 'smooth'
  });
}